import { type GroupChannelCreateParams } from '@sendbird/chat/groupChannel';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { CHANNEL_TYPE } from '../../../resources/Chat/GroupCustomChannelList/types';
import { type User } from '../../../resources/User/types';
import { type UserDto } from '../../../hooks/useGetUsers';
import { type AdminDataProvider } from '../../adminDataProvider';

type CreateChannelParams = {
  type: CHANNEL_TYPE;
  currentUser: User;
  selectedUsers: UserDto[];
};

export const useCreateChannel = (
  options?: UseMutationOptions<any, Error, CreateChannelParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();

  const createGroupFailedNotify = useCallback(
    () => notify(translate('chat.groupCreationError'), { type: 'error' }),
    [notify, translate]
  );

  const createGroupFailedMaximumChannelCountNotify = useCallback(
    () =>
      notify(translate('chat.groupCreationErrorMaximumChannelCount'), {
        type: 'error',
      }),
    [notify, translate]
  );

  const createChannel = async ({
    type,
    currentUser,
    selectedUsers,
  }: CreateChannelParams) => {
    const invitedUserIds = selectedUsers.map((su) => su.id.toString());
    const isSuper = type === CHANNEL_TYPE.SUPERGROUP;
    const params: GroupChannelCreateParams = {
      invitedUserIds,
      isSuper,
      operatorUserIds: [currentUser.id.toString()],
      name: '',
      // TODO: add coverUrl to API
      coverUrl: '',
    };

    const res = await dataProvider.postChannelCreate({
      userIds: params.invitedUserIds?.map((id) => parseInt(id)) ?? [],
      operatorIds: params.operatorUserIds?.map((id) => parseInt(id)) ?? [],
      name: params.name ?? '',
      isSuper: params.isSuper,
    });

    if (res.status === 201) {
      return res.json.channelUrl;
    } else {
      throw new Error('Failed to create channel');
    }
  };

  return useMutation({
    mutationKey: ['create-channel'],
    mutationFn: async ({ type, currentUser, selectedUsers }) => {
      return await createChannel({
        type,
        currentUser,
        selectedUsers,
      });
    },
    onError: (err: Error & { code?: number }) => {
      if (err.code === 400203) {
        createGroupFailedMaximumChannelCountNotify();
      } else {
        createGroupFailedNotify();
      }
    },
    ...options,
  });
};
